import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
	rsvpLookupEndpoint,
	rsvpSubmitEndpoint,
	IRsvpVjencanje,
	RsvpIshod
} from '../../endpoint/rsvp.ts'
import './index.css'

// Gornja granica pratnje mora pratiti MAX_PRATNJE u functions/src/rsvp/submit.js.
// Backend odbija sve iznad ovoga, pa obrazac ni ne nudi mogućnost da pošalje više.
const MAX_PRATNJA = 10

interface IPratnjaRed {
	ime: string
	prezime: string
}

const prazanRed = (): IPratnjaRed => ({ ime: '', prezime: '' })

// Datum se ispisuje na jeziku planera, ne preglednika: pozivnicu je poslao par,
// a gost je otvorio njihov link. Neispravan ili nepostojeći datum ne ruši
// stranicu nego se preskače - potvrda dolaska ne ovisi o njemu.
const formatirajDatum = (iso: string, jezik: string): string => {
	if (!iso) return ''
	const datum = new Date(iso)
	if (isNaN(datum.getTime())) return ''
	return datum.toLocaleDateString(jezik || 'hr', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	})
}

const formatirajSat = (iso: string | null, jezik: string): string => {
	if (!iso) return ''
	const datum = new Date(iso)
	if (isNaN(datum.getTime())) return ''
	return datum.toLocaleTimeString(jezik || 'hr', { hour: '2-digit', minute: '2-digit' })
}

// Javna stranica: jedini ekran koji vidi netko tko nije kupac.
//
// Stoji izvan mape jezika jer joj jezik ne dolazi iz adrese nego iz planera —
// gost otvara link mladenaca, ne bira jezik. Isti presedan kao blog/ i legal/.
//
// Nema ponude, poziva na akciju ni linka na registraciju ili kupnju bilo gdje
// na ovoj stranici. To je namjerna odluka: stranicu vidi netko tko nije kupac.
const Rsvp: React.FC = () => {
	const { token = '' } = useParams()
	const { t, i18n } = useTranslation('planner')

	const [vjencanje, setVjencanje] = useState<IRsvpVjencanje | null>(null)
	const [ucitavanje, setUcitavanje] = useState(true)
	const [ime, setIme] = useState('')
	const [prezime, setPrezime] = useState('')
	const [pratnja, setPratnja] = useState<IPratnjaRed[]>([])
	// Namjerno bez zadane vrijednosti. Dok je stranica bila neoblikovana to se
	// nije vidjelo, ali plocica koja svijetli zlatno govori "ovo si izabrao" —
	// a gost nije izabrao nista. Tko posalje ne procitavsi bio bi zabiljezen
	// kao da dolazi, a par po tom popisu slaze stolove.
	const [status, setStatus] = useState<'confirmed' | 'declined' | null>(null)
	const [ishod, setIshod] = useState<RsvpIshod | null>(null)
	const [pratnjaUpisana, setPratnjaUpisana] = useState(0)
	const [pratnjaOdbijena, setPratnjaOdbijena] = useState(0)
	const [greska, setGreska] = useState(false)
	const [salje, setSalje] = useState(false)

	useEffect(() => {
		rsvpLookupEndpoint(token)
			.then((v) => {
				setVjencanje(v)
				// Jezik para, ne jezik preglednika: pozivnicu je poslao par.
				if (v.language) i18n.changeLanguage(v.language)
			})
			.catch(() => setVjencanje(null))
			.finally(() => setUcitavanje(false))
	}, [token, i18n])

	// Ishod se veže uz ono što je bilo upisano kad je poslan. Ispravi li gost
	// tipfeler u prezimenu, "Da, dodaj me" bi ga upisalo pod novim imenom, a
	// pristanak je dao za staro — pa svaka izmjena imena vraća obrazac na
	// običan "Pošalji".
	const promijeniIme = (postavi: (v: string) => void, vrijednost: string) => {
		postavi(vrijednost)
		setIshod(null)
	}

	const dodajRed = () => {
		setPratnja((prev) => prev.length >= MAX_PRATNJA ? prev : [...prev, prazanRed()])
	}

	const ukloniRed = (indeks: number) => {
		setPratnja((prev) => prev.filter((_, i) => i !== indeks))
	}

	const promijeniRed = (indeks: number, polje: keyof IPratnjaRed, vrijednost: string) => {
		setPratnja((prev) => prev.map((red, i) => i === indeks ? { ...red, [polje]: vrijednost } : red))
	}

	// Ime, prezime i pratnja se NIKAD ne brišu ovdje. Gost koji je upisao
	// podatke i naletio na grešku mreže ne smije morati tipkati ponovno — to
	// je jedina greška zbog koje ljudi odustanu i ne vrate se.
	const posalji = async (dopustiDopunu = false) => {
		if (!status) return
		setSalje(true)
		setGreska(false)
		try {
			// Samo redovi u koje je gost nešto upisao putuju na backend; prazan red
			// dodan klikom na "Dodaj osobu", a nikad popunjen, se ne šalje.
			const popunjenaPratnja = pratnja.filter((red) => red.ime.trim() !== '' || red.prezime.trim() !== '')
			const r = await rsvpSubmitEndpoint({
				token,
				ime,
				prezime,
				status,
				dopustiDopunu,
				...(popunjenaPratnja.length > 0 ? { pratnja: popunjenaPratnja } : {})
			})
			setIshod(r.ishod)
			setPratnjaUpisana(r.pratnjaUpisana ?? 0)
			setPratnjaOdbijena(r.pratnjaOdbijena ?? 0)
		} catch {
			setGreska(true)
		} finally {
			setSalje(false)
		}
	}

	if (ucitavanje) return <div className='body-container' />

	if (!vjencanje) {
		return (
			<div className='rsvp'>
				<div className='rsvp-kartica rsvp-ishod'>
					<h1 className='rsvp-imena'>{t('rsvp.linkNijeValjan')}</h1>
					<hr className='rsvp-crta' />
					<p className='rsvp-potpis'>Wedding Camera</p>
				</div>
			</div>
		)
	}

	if (ishod === 'potvrdeno') {
		return (
			<div className='rsvp'>
				<div className='rsvp-kartica rsvp-ishod'>
					<h1 className='rsvp-imena'>{status === 'confirmed' ? t('rsvp.hvala') : t('rsvp.hvalaNeDolazite')}</h1>
					<hr className='rsvp-crta' />
				{/* Gost koji je dodao troje pratnje, a samo jedno je stiglo na
				popis, zaslužuje da to piše ovdje, ne samo "hvala". */}
				{pratnjaOdbijena > 0 && (
						<p className='rsvp-poruka'>{t('rsvp.pratnjaDjelomicno', {
							upisano: pratnjaUpisana,
							ukupno: pratnjaUpisana + pratnjaOdbijena
						})}</p>
				)}
					<p className='rsvp-potpis'>Wedding Camera</p>
				</div>
			</div>
		)
	}

	// Spec traži da stranica pokaže imena mladenaca I datum. Datum je jedini
	// podatak po kojem gost provjeri da je otvorio link svojih mladenaca.
	const datumVjencanja = formatirajDatum(vjencanje.weddingDate, vjencanje.language)

	// Par pozivnicu ispunjava kroz vise sjedanja, a link dijeli ranije. Blok se
	// pokazuje tek kad u njemu ima ista — prazan okvir s naslovima gostu ne
	// govori nista, a izgleda kao da je stranica pokvarena.
	const pozivnica = vjencanje.pozivnica ?? null

	return (
		<div className='rsvp'>
			<div className='rsvp-kartica'>
				<h1 className='rsvp-imena'>{vjencanje.brideName} &amp; {vjencanje.groomName}</h1>
				{datumVjencanja && <p className='rsvp-datum'>{datumVjencanja}</p>}
				{pozivnica && (
					<div className='rsvp-pozivnica' data-testid='rsvp-pozivnica'>
						{[pozivnica.obred, pozivnica.domjenak].map((dogadjaj, i) => (
							dogadjaj && (dogadjaj.mjesto || dogadjaj.adresa || dogadjaj.vrijeme)
? (
								<div className='rsvp-dogadjaj' key={i}>
									{dogadjaj.naziv && <h3 className='rsvp-dogadjaj-naslov'>{dogadjaj.naziv}</h3>}
									{dogadjaj.mjesto && <p className='rsvp-dogadjaj-red'>{dogadjaj.mjesto}</p>}
									{dogadjaj.adresa && <p className='rsvp-dogadjaj-red'>{dogadjaj.adresa}</p>}
									{dogadjaj.vrijeme && (
										<p className='rsvp-dogadjaj-sat'>
											{formatirajSat(dogadjaj.vrijeme, vjencanje.language)}
										</p>
									)}
								</div>
							)
: null
						))}
						{pozivnica.poruka && <p className='rsvp-pozivnica-poruka'>{pozivnica.poruka}</p>}
					</div>
				)}
				<hr className='rsvp-crta' />
				<h2 className='rsvp-naslov'>{t('rsvp.naslov')}</h2>

				<label className='rsvp-polje' htmlFor='ime'>
					<span>{t('rsvp.ime')}</span>
					<input id='ime' value={ime} onChange={(e) => promijeniIme(setIme, e.target.value)} />
				</label>

				<label className='rsvp-polje' htmlFor='prezime'>
					<span>{t('rsvp.prezime')}</span>
					<input id='prezime' value={prezime} onChange={(e) => promijeniIme(setPrezime, e.target.value)} />
				</label>

				<div className='rsvp-odluka'>
					<button
						type='button'
						className='rsvp-plocica'
						aria-pressed={status === 'confirmed'}
						onClick={() => setStatus('confirmed')}
					>{t('rsvp.dolazim')}</button>
					<button
						type='button'
						className='rsvp-plocica'
						aria-pressed={status === 'declined'}
						onClick={() => setStatus('declined')}
					>{t('rsvp.neDolazim')}</button>
				</div>

				<p className='rsvp-pratnja-naslov'>{t('rsvp.pratnja')}</p>
				{pratnja.map((red, indeks) => (
					<div className='rsvp-red' key={indeks}>
						<label className='rsvp-polje' htmlFor={`pratnja-ime-${indeks}`}>
							<span>{t('rsvp.osoba')} {indeks + 1} — {t('rsvp.ime')}</span>
							<input
								id={`pratnja-ime-${indeks}`}
								data-testid={`pratnja-ime-${indeks}`}
								value={red.ime}
								onChange={(e) => promijeniRed(indeks, 'ime', e.target.value)}
							/>
						</label>
						<label className='rsvp-polje' htmlFor={`pratnja-prezime-${indeks}`}>
							<span>{t('rsvp.osoba')} {indeks + 1} — {t('rsvp.prezime')}</span>
							<input
								id={`pratnja-prezime-${indeks}`}
								data-testid={`pratnja-prezime-${indeks}`}
								value={red.prezime}
								onChange={(e) => promijeniRed(indeks, 'prezime', e.target.value)}
							/>
						</label>
						<button
							type='button'
							className='rsvp-ukloni'
							onClick={() => ukloniRed(indeks)}
						>{t('rsvp.ukloni')}</button>
					</div>
				))}
				{pratnja.length < MAX_PRATNJA && (
					<button type='button' className='rsvp-dodaj' onClick={dodajRed}>
						{t('rsvp.dodajPratnju')}
					</button>
				)}

				{ishod === 'vise-podudaranja' && <p className='rsvp-poruka'>{t('rsvp.visePodudaranja')}</p>}
				{ishod === 'prepuno' && <p className='rsvp-poruka'>{t('rsvp.prepuno')}</p>}
				{ishod === 'prag' && <p className='rsvp-poruka'>{t('rsvp.prag')}</p>}
				{greska && <p className='rsvp-poruka'>{t('rsvp.greska')}</p>}

				{ishod === 'nepoznato'
					? (
						<>
							<p className='rsvp-poruka'>{t('rsvp.nepoznato')}</p>
							<button
								type='button'
								className='rsvp-posalji'
								disabled={salje || !status}
								onClick={() => posalji(true)}
							>{t('rsvp.dodajMe')}</button>
						</>
					)
					: (
						<button
							type='button'
							className='rsvp-posalji'
							disabled={salje || !status}
							onClick={() => posalji(false)}
						>{t('rsvp.posalji')}</button>
					)}

				<p className='rsvp-potpis'>Wedding Camera</p>
			</div>
		</div>
	)
}

export default Rsvp
